import React, { useState, useEffect } from 'react';
import { StepTracker, Step } from './StepTracker';
import { buildApiUrl } from '../config/api';

export interface ChecklistStep {
  id: string;
  number: number;
  description: string;
  category: 'user' | 'frontend' | 'network' | 'backend' | 'database' | 'storage' | 'response';
  estimatedTime: number; // in seconds
  action: () => Promise<{ success: boolean; details?: string; data?: any }>;
  dependencies?: string[]; // IDs of steps that must complete first
  retryable?: boolean;
  timeout?: number; // in milliseconds
}

export interface ChecklistConfig {
  id: string;
  title: string;
  description: string;
  estimatedTotalTime: number; // in seconds
  steps: ChecklistStep[];
  onComplete?: (results: ChecklistResults) => void;
  onStepComplete?: (stepId: string, result: any) => void;
  autoStart?: boolean;
  showCategories?: boolean;
  showTimeline?: boolean;
}

export interface ChecklistResults {
  success: boolean;
  completedSteps: number;
  totalSteps: number;
  totalTime: number;
  errors: Array<{ stepId: string; error: string }>;
  data: Record<string, any>;
}

interface ChecklistRunnerProps {
  config: ChecklistConfig;
  className?: string;
}

export const ChecklistRunner: React.FC<ChecklistRunnerProps> = ({
  config,
  className = ""
}) => {
  const [steps, setSteps] = useState<Step[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<ChecklistResults>({
    success: false,
    completedSteps: 0,
    totalSteps: config.steps.length,
    totalTime: 0,
    errors: [],
    data: {}
  });
  const [startTime, setStartTime] = useState<number>(0);

  // Initialize steps from config
  useEffect(() => {
    const initialSteps: Step[] = config.steps.map((step, index) => ({
      id: step.id,
      number: step.number,
      description: step.description,
      category: step.category,
      status: 'pending',
      duration: step.estimatedTime,
      details: `Estimated: ${step.estimatedTime}s`
    }));
    setSteps(initialSteps);

    if (config.autoStart) {
      runChecklist();
    }
  }, [config]);

  const updateStep = (stepId: string, updates: Partial<Step>) => {
    setSteps(prev => prev.map(step =>
      step.id === stepId
        ? { ...step, ...updates, timestamp: updates.status === 'success' || updates.status === 'error' ? Date.now() : step.timestamp }
        : step
    ));
  };

  const canRunStep = (step: ChecklistStep): boolean => {
    if (!step.dependencies) return true;

    return step.dependencies.every(depId => {
      const depStep = steps.find(s => s.id === depId);
      return depStep?.status === 'success';
    });
  };

  const runStep = async (step: ChecklistStep): Promise<void> => {
    if (!canRunStep(step)) {
      updateStep(step.id, {
        status: 'error',
        details: 'Dependencies not met'
      });
      return;
    }

    updateStep(step.id, {
      status: 'running',
      details: 'Executing...'
    });

    try {
      const timeout = step.timeout || 30000; // 30s default timeout
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Step timeout')), timeout)
      );

      const result = await Promise.race([
        step.action(),
        timeoutPromise
      ]);

      if (result.success) {
        updateStep(step.id, {
          status: 'success',
          details: result.details || 'Completed successfully'
        });

        // Store step result data
        setResults(prev => ({
          ...prev,
          data: { ...prev.data, [step.id]: result.data }
        }));

        // Call step completion callback
        config.onStepComplete?.(step.id, result.data);
      } else {
        throw new Error(result.details || 'Step failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      updateStep(step.id, {
        status: 'error',
        details: errorMessage
      });

      setResults(prev => ({
        ...prev,
        errors: [...prev.errors, { stepId: step.id, error: errorMessage }]
      }));

      // If retryable, we could implement retry logic here
      if (step.retryable) {
        updateStep(step.id, { details: `${errorMessage} (Retryable)` });
      }
    }
  };

  const runChecklist = async () => {
    if (isRunning) return;

    setIsRunning(true);
    setStartTime(Date.now());
    setResults(prev => ({ ...prev, errors: [], data: {} }));

    // Run steps sequentially, respecting dependencies
    for (const configStep of config.steps) {
      if (!isRunning) break; // Allow stopping

      // Wait for dependencies
      while (!canRunStep(configStep) && isRunning) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (isRunning) {
        await runStep(configStep);
      }
    }

    // Calculate final results
    const endTime = Date.now();
    const completedSteps = steps.filter(s => s.status === 'success').length;
    const finalResults: ChecklistResults = {
      success: completedSteps === config.steps.length,
      completedSteps,
      totalSteps: config.steps.length,
      totalTime: Math.round((endTime - startTime) / 1000),
      errors: results.errors,
      data: results.data
    };

    setResults(finalResults);
    setIsRunning(false);

    // Call completion callback
    config.onComplete?.(finalResults);
  };

  const retryStep = async (stepId: string) => {
    const configStep = config.steps.find(s => s.id === stepId);
    if (configStep && configStep.retryable) {
      await runStep(configStep);
    }
  };

  const retryFailedSteps = async () => {
    const failedSteps = steps.filter(s => s.status === 'error');
    for (const step of failedSteps) {
      const configStep = config.steps.find(s => s.id === step.id);
      if (configStep?.retryable) {
        await runStep(configStep);
      }
    }
  };

  const resetChecklist = () => {
    setSteps(prev => prev.map(step => ({
      ...step,
      status: 'pending' as const,
      details: `Estimated: ${config.steps.find(s => s.id === step.id)?.estimatedTime}s`,
      timestamp: undefined
    })));
    setIsRunning(false);
    setResults({
      success: false,
      completedSteps: 0,
      totalSteps: config.steps.length,
      totalTime: 0,
      errors: [],
      data: {}
    });
  };

  const stopChecklist = () => {
    setIsRunning(false);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header with Controls */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold">{config.title}</h2>
            <p className="text-gray-600 text-sm">{config.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              Est. {Math.floor(config.estimatedTotalTime / 60)}m {config.estimatedTotalTime % 60}s
            </span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={runChecklist}
            disabled={isRunning}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isRunning ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Running...
              </>
            ) : (
              <>
                ▶️ Start Checklist
              </>
            )}
          </button>

          {isRunning && (
            <button
              onClick={stopChecklist}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              ⏹️ Stop
            </button>
          )}

          <button
            onClick={resetChecklist}
            disabled={isRunning}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
          >
            🔄 Reset
          </button>

          {results.errors.length > 0 && (
            <button
              onClick={retryFailedSteps}
              disabled={isRunning}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
            >
              🔁 Retry Failed
            </button>
          )}
        </div>

        {/* Progress Summary */}
        <div className="mt-4 grid grid-cols-4 gap-4 text-center">
          <div className="p-2 bg-blue-50 rounded">
            <div className="text-lg font-bold text-blue-600">{results.completedSteps}</div>
            <div className="text-xs text-gray-600">Completed</div>
          </div>
          <div className="p-2 bg-gray-50 rounded">
            <div className="text-lg font-bold text-gray-600">{results.totalSteps}</div>
            <div className="text-xs text-gray-600">Total Steps</div>
          </div>
          <div className="p-2 bg-green-50 rounded">
            <div className="text-lg font-bold text-green-600">{results.totalTime}s</div>
            <div className="text-xs text-gray-600">Time Elapsed</div>
          </div>
          <div className="p-2 bg-red-50 rounded">
            <div className="text-lg font-bold text-red-600">{results.errors.length}</div>
            <div className="text-xs text-gray-600">Errors</div>
          </div>
        </div>
      </div>

      {/* Step Tracker */}
      <StepTracker
        title="Step Details"
        steps={steps}
        showTimeline={config.showTimeline}
        showCategories={config.showCategories}
        onStepUpdate={(stepId, status, details) => {
          if (status === 'error') {
            retryStep(stepId);
          }
        }}
      />

      {/* Results */}
      {(results.completedSteps > 0 || results.errors.length > 0) && (
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="font-semibold mb-3">Results</h3>

          {results.success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-3">
              <div className="flex items-center gap-2">
                <span className="text-green-600">✅</span>
                <span className="font-medium text-green-800">Checklist completed successfully!</span>
              </div>
              <div className="text-sm text-green-700 mt-1">
                All {results.totalSteps} steps completed in {results.totalTime} seconds.
              </div>
            </div>
          )}

          {results.errors.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-red-600">❌</span>
                <span className="font-medium text-red-800">Errors encountered:</span>
              </div>
              <ul className="space-y-1">
                {results.errors.map((error, index) => (
                  <li key={index} className="text-sm text-red-700">
                    <strong>{error.stepId}:</strong> {error.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Object.keys(results.data).length > 0 && (
            <div className="mt-3">
              <h4 className="font-medium mb-2">Collected Data:</h4>
              <pre className="bg-gray-50 p-3 rounded text-sm overflow-auto max-h-40">
                {JSON.stringify(results.data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};